import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  Paper,
  Grid,
  TableCell,
  TableBody,
  TableRow,
  Table,
  TableContainer,
  Button,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/CloseRounded";
import Loading from "../../../../../components/Loading";
import DataTable from "../../../../../components/DataTable";

const TngReportDetailDialogComponent = ({
  handleClose,
  isFetching,
  data,
  onExport,
}) => {
  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick") return;
    handleClose();
  };

  const transactions = Array.isArray(data?.transactions)
    ? data.transactions
    : [];

  const getCellStyle = (row) => ({});

  const getIntegrityCheckStyle = (row) => {
    if (row.transactionValidStatus === "Pass") {
      return { color: "#4caf50", fontWeight: "bold" };
    }
    if (row.transactionValidStatus === "Failed") {
      return { color: "#f44336", fontWeight: "bold" };
    }
    return {};
  };

  const getDiscrepancyStyle = (row) => {
    if (row.is_missing) {
      return { color: "red" };
    }
    if (row.is_unauthorized) {
      return { color: "red" };
    }
    if (row.is_duplicate) {
      return { color: "red" };
    }
    if (row.is_status_mismatch) {
      return { color: "red" };
    }
    if (row.is_missing_participant) {
      return { color: "red" };
    }
    return {};
  };

  const getIntegrityStatusStyle = (status) => {
    if (status === "Pass") {
      return { color: "#4caf50", fontWeight: "bold" };
    }
    if (status === "Failed") {
      return { color: "#f44336", fontWeight: "bold" };
    }
    return {};
  };

  const canExport =
    !isFetching && Array.isArray(transactions) && transactions.length > 0;

  // tiny helper to parse body_info safely
  const safeParse = (s) => {
    try {
      if (!s) return {};
      return typeof s === "string" ? JSON.parse(s) : s;
    } catch {
      return {};
    }
  };

  return (
    <Dialog
      open
      onClose={handleDialogClose}
      scroll="paper"
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle id="scroll-dialog-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">
            <b>Report Details</b>
          </Typography>
          <IconButton
            size="small"
            color="inherit"
            onClick={handleClose}
            style={{ padding: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        style={{ backgroundColor: "#f3f3f3", padding: 16 }}
      >
        <Paper>
          {isFetching ? (
            <Loading />
          ) : (
            <Box p={2} display="flex" flexDirection="column">
              <Box display="flex" justifyContent="space-between">
                <Box flex={1}>
                  <Box mt={2}>
                    <Grid container>
                      <Grid item md={6} style={{ width: "100%" }}>
                        <Typography variant="h6" color="textSecondary">
                          Report Date:
                        </Typography>
                      </Grid>
                      <Grid item md={6} style={{ width: "100%" }}>
                        <Typography variant="h6">
                          {data?.date || data?.identifier || "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box mt={1}>
                    <Grid container>
                      <Grid item md={6} style={{ width: "100%" }}>
                        <Typography variant="h6" color="textSecondary">
                          Report Status:
                        </Typography>
                      </Grid>
                      <Grid item md={6} style={{ width: "100%" }}>
                        <Typography variant="h6">
                          {data?.status || "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box mt={1}>
                    <Grid container>
                      <Grid item md={6} style={{ width: "100%" }}>
                        <Typography variant="h6" color="textSecondary">
                          Integrity Check:
                        </Typography>
                      </Grid>
                      <Grid item md={6} style={{ width: "100%" }}>
                        <Typography
                          variant="h6"
                          style={getIntegrityStatusStyle(
                            data?.transactionValidStatus
                          )}
                        >
                          {data?.transactionValidStatus || "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>

                <Box ml={3} mt={2}>
                  <TableContainer component={Paper} style={{ maxWidth: 600 }}>
                    <Table sx={{ minWidth: 450 }}>
                      <TableBody>
                        <TableRow>
                          <TableCell>Metrics</TableCell>
                          <TableCell align="center">
                            Disbursed by SmartKood
                          </TableCell>
                          <TableCell align="center">
                            Reported by Provider
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Amount (RM)</TableCell>
                          <TableCell align="center">
                            {data?.totalAmount ?? "-"}
                          </TableCell>
                          <TableCell align="center">
                            {data?.csvAmount ?? "-"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Transaction Count</TableCell>
                          <TableCell align="center">
                            {data?.transactionInitializedCount ?? "-"}
                          </TableCell>
                          <TableCell align="center">
                            {data?.transactionCompletedCount ?? "-"}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>

              <Box mt={3}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Transaction Breakdown
                </Typography>

                <DataTable
                  isLoading={isFetching}
                  rows={transactions}
                  rowsPerPage={transactions.length}
                  total={transactions.length}
                  page={0}
                  renderHeader={() => (
                    <>
                      <TableCell>No.</TableCell>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Amount (RM)</TableCell>
                      <TableCell>Disbursement Id</TableCell>
                      <TableCell>Mobile No.</TableCell>
                      <TableCell>Company Name</TableCell>
                      <TableCell>Campaign Type</TableCell>
                      <TableCell>Integrity Check</TableCell>
                      <TableCell>Discrepancy Details</TableCell>
                    </>
                  )}
                  renderRow={(row, index) => {
                    const bodyInfo = safeParse(row.body_info);
                    const disbId = row.is_missing
                      ? "N/A"
                      : bodyInfo?.disbursementId ?? "N/A";
                    const amountCents = Number.isFinite(+row?.amount)
                      ? Number(row.amount)
                      : 0;
                    const amountRM = (amountCents / 100).toFixed(2);
                    return (
                      <>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell style={getCellStyle(row)}>
                          {row.completed_timestamp}
                        </TableCell>
                        <TableCell style={getCellStyle(row)}>
                          {amountRM}
                        </TableCell>
                        <TableCell style={getCellStyle(row)}>
                          {disbId}
                        </TableCell>
                        <TableCell style={getCellStyle(row)}>
                          {row.transaction_destination}
                        </TableCell>
                        <TableCell style={getCellStyle(row)}>
                          {row.company_name}
                        </TableCell>
                        <TableCell style={getCellStyle(row)}>
                          {row.lucky_draw_type}
                        </TableCell>
                        <TableCell style={getIntegrityCheckStyle(row)}>
                          {row.transactionValidStatus}
                        </TableCell>
                        <TableCell style={getDiscrepancyStyle(row)}>
                          {row.is_missing
                            ? row.status
                            : row.is_unauthorized
                            ? row.status
                            : row.is_duplicate
                            ? row.status
                            : row.is_status_mismatch
                            ? row.status
                            : row.is_missing_participant
                            ? row.status
                            : "-"}
                        </TableCell>
                      </>
                    );
                  }}
                  onPageClick={() => {}}
                  handleItemPerPageChange={() => {}}
                />
              </Box>
            </Box>
          )}
        </Paper>
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
          mb={3}
          mt={3}
          flexWrap="wrap"
        >
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Export as:
            </Typography>
            <Button
              variant="contained"
              color="primary"
              disabled={!canExport}
              onClick={() => onExport?.("csv")}
            >
              CSV
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!canExport}
              onClick={() => onExport?.("xlsx")}
            >
              EXCEL
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!canExport}
              onClick={() => onExport?.("txt")}
            >
              TXT
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TngReportDetailDialogComponent;
